import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Button,
  Stack,
  useTheme,
  Box
} from "@mui/material";
import {
  Edit20Px,
  LeafIcon,
  SquareIconButton,
  useToast
} from "@qubit/autoparts";

import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { ActivityState, AutostoreBin } from "~/features/autostoreBin";
import { AlertInfo } from "~/features/inventory/components/AlertInfo";
import {
  mixpanelTrack,
  mixpanelTrackModalDismiss,
  useMixpanelModalName,
  useMixpanelPageName
} from "~/lib/mixpanel-tracking";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { QUANTITY_MAX_VALUE } from "~/lib/shared";
import { generateInventoryReasonCodeOptions } from "~/redux/actions/inventory";

import { selectUsersFulfillmentCenter } from "~/redux/selectors/storeSelectors";
import {
  selectIsShortSideBin,
  selectWorkstationAutostoreGridId,
  selectWorkstationId
} from "~/redux/selectors/workstationsSelectors";
import { usePatchInventoryNetAdjustmentMutation } from "~/redux/warehouse/inventory.hooks";

import { InventoryDto } from "~/types/api";

import { setIsMoveAdjustModalOpen } from "./binMaintenanceWorkstation.slice";

type AdjustButtonProps = {
  portId: number;
  state: ActivityState;
  binId: number | undefined;
  numberOfRows: number;
  numberOfColumns: number;
  compartmentNumber?: number;
  binInventory: InventoryDto[] | undefined;
  skipMove: () => void;
  /**
   * If true, the user will be prompted to remove all inventory. Qty field will be disabled. Reason code will be set to "Sanitization Status Unknown".
   */
  clearAllInventory?: boolean;
  closeMoveInventoryModal: () => void;
};

export function AdjustButton(props: AdjustButtonProps) {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const { errorToast, successToast } = useToast();
  const workstationId = useAppSelector(selectWorkstationId);
  const autostoreGridId = useAppSelector(selectWorkstationAutostoreGridId);
  const usersFulfillmentCenter = useAppSelector(selectUsersFulfillmentCenter);
  const [patchNetAdjustment, { isLoading: isConfirmLoading }] =
    usePatchInventoryNetAdjustmentMutation();
  const { palette } = useTheme();
  const {
    portId,
    state,
    binId,
    numberOfRows,
    numberOfColumns,
    binInventory,
    compartmentNumber,
    clearAllInventory = false,
    closeMoveInventoryModal
  } = props;
  const isShortSideBin = useAppSelector(selectIsShortSideBin);
  const alertMessageHeader = t("attention sanitize item");
  const alertMessage = t("sanitization status of bin is unknown");

  const trackedPageName = useMixpanelPageName();
  const trackedModalName = useMixpanelModalName(
    clearAllInventory ? "Remove Quantity" : "Adjust Quantity"
  );

  const inventoryToMove = binInventory?.find(
    (inv) => inv.bin.autostoreCompartmentNumber === compartmentNumber
  );
  const clientFCReasonCodes =
    usersFulfillmentCenter?.clientConfiguration
      ?.clientSpecificInventoryAdjustmentReasonCodes;

  const initialQty = inventoryToMove?.count.value ?? null;
  const isModalOpen = useAppSelector(
    (state) => state.binMaintenanceWorkstation.isMoveAdjustModalOpen
  );
  const [currentQuantity, setCurrentQuantity] = useState<number | null>(null);
  const [reasonCode, setReasonCode] = useState<string>("Counted");

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler, react-you-might-not-need-an-effect/no-derived-state
    if (isModalOpen && !clearAllInventory) setCurrentQuantity(initialQty);
  }, [isModalOpen, initialQty, clearAllInventory]);

  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (clearAllInventory) {
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setCurrentQuantity(0);
      // eslint-disable-next-line react-you-might-not-need-an-effect/no-adjust-state-on-prop-change
      setReasonCode("Sanitization Status Unknown");
    }
  }, [clearAllInventory]);

  const reasonCodeField = (
    <TextField
      id="reasonCode"
      label={t("reason code")}
      type="string"
      select
      fullWidth
      variant="outlined"
      value={reasonCode || ""}
      disabled={clearAllInventory}
      InputLabelProps={{
        shrink: true
      }}
      inputProps={{
        sx: { fontSize: "1rem" }
      }}
      SelectProps={{
        onChange: (e) => {
          setReasonCode(e.target.value as string);
          mixpanelTrack({
            type: "Select Option",
            label: "Update Reason Code",
            eventProperties: {
              "Reason Code": e.target.value
            },
            trackedPageName: trackedModalName
          });
        }
      }}
    >
      {generateInventoryReasonCodeOptions(clientFCReasonCodes).map(
        (choiceObj, i, arr) => (
          <MenuItem
            key={`uom-option-${choiceObj.value}`}
            className={`uom-option-${i}`}
            sx={{
              padding: 1,
              fontSize: "24px",
              borderBottomColor: "gray.light",
              borderBottom: i === arr.length - 1 ? "none" : `1px solid`
            }}
            value={choiceObj.value}
          >
            {choiceObj.label}
          </MenuItem>
        )
      )}
    </TextField>
  );

  const netAdjustmentQty =
    currentQuantity !== null &&
    initialQty !== null &&
    currentQuantity - initialQty;

  const isDecreaseQuantityDisabled =
    clearAllInventory || currentQuantity == null || currentQuantity <= 0;

  let confirmText = t("confirm");
  if (netAdjustmentQty && netAdjustmentQty > 0) {
    confirmText = t("add") + ` ${netAdjustmentQty}`;
  } else if (netAdjustmentQty && netAdjustmentQty < 0) {
    confirmText = t("remove") + ` ${netAdjustmentQty * -1}`;
  }

  const handleConfirm = async () => {
    if (!inventoryToMove || netAdjustmentQty === false) return;
    try {
      const netAdjustment = {
        units: inventoryToMove.count.units,
        value: netAdjustmentQty
      };

      await patchNetAdjustment({
        autostoreGridId,
        autostorePortId: portId,
        inventoryId: inventoryToMove.inventoryId,
        netAdjustment,
        reasonCode,
        workstationId
      }).unwrap();

      successToast(t("inventory adjusted"));
      dispatch(setIsMoveAdjustModalOpen(false));
      const adjustedToZero = currentQuantity === 0;
      if (adjustedToZero) {
        props.skipMove();
      }
    } catch (err: unknown) {
      errorToast(getMessageFromRtkError(err), {
        trackingLocation: trackedModalName
      });
    }
  };

  return (
    <>
      <Button
        startIcon={<Edit20Px />}
        size="large"
        onClick={() => {
          dispatch(setIsMoveAdjustModalOpen(true));
          mixpanelTrack({
            type: "Button Click",
            label: "Adjust",
            trackedPageName,
            eventProperties: {
              "Bin Number": binId,
              Compartment: compartmentNumber,
              "Port Id": portId
            }
          });
        }}
        variant="subtle"
      >
        {t("adjust")}
      </Button>
      <Dialog
        open={isModalOpen}
        maxWidth="xl"
        onClose={(_event, reason) => {
          mixpanelTrackModalDismiss(trackedModalName, reason);
          dispatch(setIsMoveAdjustModalOpen(false));
          if (clearAllInventory) closeMoveInventoryModal();
        }}
      >
        <DialogTitle>
          {clearAllInventory ? t("remove quantity") : t("adjust quantity")}
        </DialogTitle>
        <DialogContent sx={{ margin: "30px 30px 0" }}>
          {clearAllInventory && (
            <AlertInfo
              message={alertMessage}
              messageHeader={alertMessageHeader}
              type="sanitize"
              icon={<LeafIcon sx={{ color: palette.infoPurple.dark }} />}
            />
          )}
          <Stack direction="row" spacing={2} pt={1} mb={4}>
            <TextField
              label={t("quantity")}
              type="number"
              variant="outlined"
              value={currentQuantity}
              disabled={clearAllInventory}
              onChange={(e) => {
                const newValue = +e.target.value;
                if (newValue > QUANTITY_MAX_VALUE) {
                  return;
                }
                setCurrentQuantity(newValue);
              }}
              onBlur={(e) => {
                if (e.target.value !== "") {
                  mixpanelTrack({
                    type: "Input Change",
                    label: "Manual Quantity Input",
                    eventProperties: {
                      Quantity: e.target.value,
                      "Initial Quantity": initialQty
                    },
                    trackedPageName: trackedModalName
                  });
                }
              }}
              style={{ margin: "auto 0" }}
              slotProps={{
                htmlInput: {
                  role: "spinbutton",
                  sx: {
                    width: "200px",
                    textAlign: "center",
                    fontSize: 50,
                    padding: "35px 10px 35px 18px"
                  },
                  min: 0,
                  step: 1
                }
              }}
            />
            <Stack gap={2} justifyContent="center">
              <SquareIconButton
                aria-label="increase quantity"
                onClick={(): void => {
                  if (clearAllInventory) return;
                  const newQty = (currentQuantity ?? 0) + 1;
                  if (newQty > QUANTITY_MAX_VALUE) return;
                  setCurrentQuantity(newQty);
                  mixpanelTrack({
                    type: "Button Click",
                    label: "Increase Quantity",
                    eventProperties: {
                      Quantity: newQty,
                      "Initial Quantity": initialQty
                    },
                    trackedPageName: trackedModalName
                  });
                }}
                disabled={
                  clearAllInventory ||
                  (currentQuantity !== null &&
                    currentQuantity >= QUANTITY_MAX_VALUE)
                }
                size="large"
              >
                <ExpandLessIcon
                  style={{
                    fontSize: 27,
                    fill:
                      clearAllInventory ||
                      (currentQuantity !== null &&
                        currentQuantity >= QUANTITY_MAX_VALUE)
                        ? "gray"
                        : "black"
                  }}
                />
              </SquareIconButton>

              <SquareIconButton
                aria-label="decrease quantity"
                onClick={(): void => {
                  if (isDecreaseQuantityDisabled) return;

                  const newQuantity = currentQuantity - 1;
                  setCurrentQuantity(newQuantity);

                  mixpanelTrack({
                    type: "Button Click",
                    label: "Decrease Quantity",
                    eventProperties: {
                      Quantity: newQuantity,
                      "Initial Quantity": initialQty
                    },
                    trackedPageName: trackedModalName
                  });
                }}
                disabled={isDecreaseQuantityDisabled}
                size="large"
              >
                <ExpandMoreIcon
                  style={{
                    fontSize: 27,
                    fill: isDecreaseQuantityDisabled ? "gray" : "black"
                  }}
                />
              </SquareIconButton>
            </Stack>
            {currentQuantity !== null && inventoryToMove && (
              <Box minWidth="300px">
                <AutostoreBin
                  state={state}
                  binId={binId}
                  pickQuantity={currentQuantity}
                  pickCompartment={
                    inventoryToMove.bin.autostoreCompartmentNumber
                      ? inventoryToMove.bin.autostoreCompartmentNumber - 1
                      : null
                  }
                  numberOfRows={numberOfRows}
                  numberOfColumns={numberOfColumns}
                  highlightActiveCompartment={true}
                  isShortSideBin={isShortSideBin}
                />
              </Box>
            )}
          </Stack>
          {clearAllInventory ? null : reasonCodeField}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", padding: "30px" }}>
          <Button
            size="large"
            variant="subtle"
            onClick={() => {
              dispatch(setIsMoveAdjustModalOpen(false));
              mixpanelTrack({
                type: "Button Click",
                label: "Cancel",
                trackedPageName: trackedModalName
              });
              if (clearAllInventory) closeMoveInventoryModal();
            }}
          >
            {t("cancel")}
          </Button>
          <Button
            size="large"
            onClick={async () => {
              await handleConfirm();
              mixpanelTrack({
                type: "Button Click",
                label: "Confirm Quantity Adjustment",
                eventProperties: {
                  Quantity: currentQuantity,
                  "Initial Quantity": initialQty,
                  "Reason Code": reasonCode
                },
                trackedPageName: trackedModalName
              });
            }}
            disabled={
              isConfirmLoading || !inventoryToMove || currentQuantity === null
            }
          >
            {confirmText}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
